import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Set / reset password page.
 *
 * Reads `?token=...` from the URL. The same component handles both the
 * welcome-flow setup link and the forgot-password reset link — the
 * backend accepts both token kinds.
 *
 * On success the API sets a session cookie, so we land the user
 * directly at /members/library.
 */
export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Token completely missing — there's nothing this page can do.
  // Send the user to /members where they can request a fresh link.
  useEffect(() => {
    if (!token) {
      navigate('/members?reason=missing_token', { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErrorMessage('');

    if (password.length < 10) {
      setErrorMessage('Password must be at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setErrorMessage('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/members-set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        navigate(data.redirect || '/members/library', { replace: true });
        return;
      }
      setErrorMessage(
        data.error ||
          'Could not save your password. The link may have expired — please request a new one from the sign-in page.'
      );
    } catch (_err) {
      setErrorMessage('Network error. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={kickerStyle}>Members Area</div>
        <h1 style={headingStyle}>Set your password</h1>
        <p style={subheadingStyle}>
          Choose a password of at least 10 characters. Once set, you'll
          sign in with this password and your subscription email.
        </p>

        {errorMessage && <div style={errorBoxStyle}>{errorMessage}</div>}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={passwordWrapperStyle}>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 10 characters)"
              autoComplete="new-password"
              disabled={submitting}
              style={inputWithToggleStyle}
              onFocus={(e) => (e.target.style.borderColor = '#4a9eff')}
              onBlur={(e) =>
                (e.target.style.borderColor = 'rgba(74, 158, 255, 0.25)')
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={eyeButtonStyle}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          <div style={passwordWrapperStyle}>
            <input
              type={showConfirm ? 'text' : 'password'}
              required
              minLength={10}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              autoComplete="new-password"
              disabled={submitting}
              style={inputWithToggleStyle}
              onFocus={(e) => (e.target.style.borderColor = '#4a9eff')}
              onBlur={(e) =>
                (e.target.style.borderColor = 'rgba(74, 158, 255, 0.25)')
              }
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              style={eyeButtonStyle}
              tabIndex={-1}
            >
              {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          <button
            type="submit"
            disabled={submitting || password.length < 10 || password !== confirm}
            style={primaryButtonStyle(submitting)}
          >
            {submitting ? 'Saving…' : 'Save password and sign in'}
          </button>
        </form>

        <p style={footerLinkStyle}>
          <Link to="/members" style={subscribeLinkStyle}>
            ← Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ---------- icons ---------- */

function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ---------- styles (mirror Members.jsx) ---------- */

const pageStyle = {
  minHeight: 'calc(100vh - 80px)',
  padding: '4rem 2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const cardStyle = { maxWidth: '520px', width: '100%', textAlign: 'center' };
const kickerStyle = {
  fontSize: '0.85rem',
  color: '#4a9eff',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginBottom: '1rem',
  fontWeight: 600,
};
const headingStyle = {
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '1rem',
  lineHeight: 1.2,
};
const subheadingStyle = {
  color: '#a8c5e6',
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: '2rem',
};
const errorBoxStyle = {
  padding: '1rem 1.25rem',
  background: 'rgba(255, 80, 80, 0.08)',
  border: '1px solid rgba(255, 80, 80, 0.25)',
  borderRadius: '8px',
  color: '#ffb8b8',
  fontSize: '0.9rem',
  marginBottom: '1.25rem',
  textAlign: 'left',
  lineHeight: 1.5,
};
const formStyle = { display: 'flex', flexDirection: 'column', gap: '1rem' };
const inputStyle = {
  width: '100%',
  padding: '1rem 1.25rem',
  fontSize: '1rem',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(74, 158, 255, 0.25)',
  borderRadius: '8px',
  color: '#ffffff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};
const passwordWrapperStyle = { position: 'relative', width: '100%' };
const inputWithToggleStyle = {
  ...inputStyle,
  paddingRight: '3rem',
};
const eyeButtonStyle = {
  position: 'absolute',
  right: '0.85rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  color: '#6b9bc9',
  cursor: 'pointer',
  padding: '0.4rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const primaryButtonStyle = (submitting) => ({
  padding: '1.1rem 2rem',
  background: submitting
    ? 'rgba(74, 158, 255, 0.4)'
    : 'linear-gradient(135deg, #4a9eff 0%, #1a6ed8 100%)',
  border: 'none',
  borderRadius: '8px',
  color: '#ffffff',
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: submitting ? 'wait' : 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: submitting ? 'none' : '0 4px 20px rgba(74, 158, 255, 0.3)',
});
const footerLinkStyle = { marginTop: '2rem', fontSize: '0.9rem' };
const subscribeLinkStyle = {
  color: '#4a9eff',
  textDecoration: 'none',
  fontWeight: 600,
};
